import React, { useState } from "react";
import OnboardingScreen, { OnboardingErrorText } from "../OnboardingScreen";
import { OnboardingGlassButton, OnboardingPrimaryButton } from "../OnboardingButtons";
import { notificationService } from "../../../services/notificationService";
import { DEFAULT_REMINDER_HOUR } from "../onboardingCopy";

interface NotificationsStepProps {
  submitting: boolean;
  submitError: string | null;
  onBack: () => void;
  /** `reminderHour` is null when the user skips. */
  onFinish: (reminderHour: number | null) => void;
}

const pad = (n: number) => n.toString().padStart(2, "0");

/**
 * Daily reminder step. "Set notification" asks for browser notification
 * permission first (iOS: UNUserNotificationCenter.requestAuthorization) and
 * continues regardless of the answer; the reminder writes happen at the plan
 * step's finish and are best-effort.
 */
const NotificationsStep: React.FC<NotificationsStepProps> = ({ submitting, submitError, onBack, onFinish }) => {
  const [time, setTime] = useState(`${pad(DEFAULT_REMINDER_HOUR)}:00`);
  const [busy, setBusy] = useState(false);
  const permission = notificationService.getPermissionState();

  const requestAndFinish = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!permission.granted) {
        try {
          await notificationService.requestPermission();
        } catch (error) {
          console.warn("[Onboarding] notification permission not granted:", error);
        }
      }
      const hour = Number.parseInt(time.split(":")[0] ?? "", 10);
      onFinish(Number.isFinite(hour) ? hour : DEFAULT_REMINDER_HOUR);
    } finally {
      setBusy(false);
    }
  };

  return (
    <OnboardingScreen
      onBack={onBack}
      footer={
        <div className="flex flex-col gap-3">
          <OnboardingPrimaryButton label={submitting ? "Finishing…" : "Set notification"} enabled={!submitting && !busy} onClick={requestAndFinish} />
          <OnboardingGlassButton label="Skip for now" enabled={!submitting && !busy} onClick={() => onFinish(null)} />
        </div>
      }
    >
      <div className="flex flex-col gap-7">
        <div className="flex flex-col gap-3">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="mb-1 text-peak-text" aria-hidden="true">
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
          <h1 className="font-funnel text-[32px] font-bold leading-[1.1] tracking-[-0.6px] text-peak-text">Set a daily reminder.</h1>
          <p className="font-geist text-[15px] text-peak-text-secondary">Consistency is what builds the baselines and ensures accuracy.</p>
        </div>
        <label className="flex flex-col gap-2">
          <span className="font-funnel text-[11px] font-medium text-peak-text-tertiary">Daily reminder time</span>
          <input
            type="time"
            value={time}
            step={3600}
            onChange={(e) => e.target.value && setTime(e.target.value)}
            className="min-h-[60px] w-full rounded-lg border border-peak-border bg-peak-bg-secondary px-4 font-geist text-[22px] font-semibold text-peak-text outline-none focus:border-peak-text"
          />
        </label>
        {permission.denied && (
          <p className="font-funnel text-[13px] text-peak-text-secondary">
            Notifications are blocked in this browser. You can still set a time now and enable them later in Settings → Notifications.
          </p>
        )}
        <OnboardingErrorText>{submitError}</OnboardingErrorText>
      </div>
    </OnboardingScreen>
  );
};

export default NotificationsStep;
